import { PermissionsView } from '@app/modules/admin/components/permission/view/PermissionsView';
import { UserStationPermissionItem } from '@app/modules/admin/components/stations-permissions/UserStationPermissionItem';
import { UserPermissionForStationState } from '@app/modules/admin/types';
import { User, UserRole } from '@mi-front/core';
import { AsyncView, NullState } from '@mi-front/ui';
import { FormattedMessage, useIntl } from 'react-intl';
import { fetchUserPermissionsForAll } from '../../../../api/permissions';
import { ClientAdminAllStationsAccessAlert } from './ClientAdminAllStationsAccessAlert';

interface Props {
  readonly user: User;
}

/**
 * Station access manager for a user. CLIENT_ADMIN users get an info alert instead
 * of per-station toggles, since they already have access to all stations.
 */
export function UserPermissionsManager({ user }: Props) {
  const { $t } = useIntl();

  if (user.role === UserRole.CLIENT_ADMIN) {
    return <ClientAdminAllStationsAccessAlert />;
  }

  return (
    <AsyncView promiseFn={() => fetchUserPermissionsForAll(user.id)}>
      {(vp) => {
        const permissionsState = vp.data;

        return (
          <PermissionsView
            permissionsState={permissionsState}
            canModify={() => true}
            filterFn={(pState, text) => {
              return !!pState.station.name.match(new RegExp(text, 'i'));
            }}
            emptyListMsg={
              <NullState>
                <FormattedMessage
                  id="admin.users.viewScreen.permissions.noFilterMatch"
                  defaultMessage="No se encontraron emisoras coincidentes"
                />
              </NullState>
            }
            filterPlaceholder={$t({
              id: 'admin.users.viewScreen.permissions.filterPlaceholder',
              defaultMessage: 'Filtrar emisoras',
            })}
            PermissionItemComponent={({
              permissionState,
            }: {
              permissionState: UserPermissionForStationState;
            }) => <UserStationPermissionItem permissionState={permissionState} user={user} />}
          />
        );
      }}
    </AsyncView>
  );
}
